import { cloneDeep, debounce } from 'lodash';
import queryString from 'querystring';
import React, { Component } from 'react';

import analyticService from '../../../services/analytics';
import { getClass } from '../../../utils/component-helpers';
import { buildQueryString } from '../../base/common/utils';
import { dateService } from '../../../services/date';
import searchService from './search-service';

import InsightsPanel from './insights-panel';
import CartExplorer from './cart-explorer/cart-explorer';
import ProductResearch from './product-research/product-research';
import MerchantCarousel from './merchant-carousel';
import SearchTrends from './search-trends/search-trends';
import TrendFilter from './trend-filter';
import SortDropdown from '../../base/dropdowns/sort-dropdown';
import MerchantSelectModal from './product-research/merchant-select-modal';
import DateToggle from './product-research/toggle';

import './cart-explorer/cart-explorer.scss';
import './product-research/product-research.scss';
import './search-trends/search-trends.scss';
import './insights.scss';

// This component renders either Cart Explorer or Search trends
class Insights extends Component {
  constructor(props) {
    super(props);

    this.AnalyticService = analyticService;
    this.dateService = dateService;
    this.service = searchService;
    this.insightsType = this.props.location.pathname.split('/').pop();

    const dateRange = this.dateService.getPresetDateRanges().thirtyDay;
    const oneDayRange = this.dateService.getPresetDateRanges().oneDay;

    this.tabOptions = [
      { label: 'Best Sellers', name: 'all', noResult: 'product-trends' },
      { label: 'Items on Sale', name: 'sale', noResult: 'items-on-sale' },
      { label: 'New Products', name: 'new', noResult: 'new-products' },
    ];

    this.sortOptions = [
      {
        label: 'Purchased',
        name: 'total_quantity',
        direction: 'desc',
        selected: true,
        identifier: 'quantity-desc',
      },
      /*
       * Price is usually feed_price_usd, but default to unit_price_usd
       * if feed price isn't available.
       */
      {
        label: 'Price - high to low',
        name: 'sale_price',
        direction: 'desc',
        selected: false,
        identifier: 'price-desc',
      },
      {
        label: 'Price - low to high',
        name: 'sale_price',
        direction: 'asc',
        selected: false,
        identifier: 'price-asc',
      },
      {
        label: 'Discounts',
        name: 'sale_percent',
        direction: 'desc',
        selected: false,
        identifier: 'sale-desc',
      },
      {
        label: 'Date Added',
        name: 'datetime_created',
        direction: 'desc',
        selected: false,
        identifier: 'datetime-desc',
      },
    ];
    this.defaultSort = 'total_quantity';
    this.dateRangeItems = [
      { label: 'Last 24 Hours', name: 'twentyFourHour', unit: 'hour', selected: true },
      { label: 'Last 30 Days', name: 'thirtyDay', unit: 'day', selected: false },
    ];

    this.state = {
      merchants: [], // All merchants with 'selected' attribute
      searchTerm: null,
      currentDateRange: '24h',
      // Dates must be last 30 days range
      params: {
        date_from: dateRange.startDate.format('YYYY-MM-DD'),
        date_to: dateRange.endDate.format('YYYY-MM-DD'),
      },
      // Params used for cart-siblings in product research if 24h is selected
      oneDayParams: {
        date_from: oneDayRange.startDate.format('YYYY-MM-DD'),
        date_to: oneDayRange.endDate.format('YYYY-MM-DD'),
      },
      // State for trend totals
      searchData: {},
      productData: {},
      showMerchantModal: false,

      showNoResult: false,
      loading: false,
      scrollable: false, // Whether we can scroll tables
      sortOptions: this.sortOptions,
      currentTab: 'all',
      dateRangeItems: this.dateRangeItems,

      resetCarousel: false,
    };

    // Merchants excluded from carousel picker / dropdown
    this.hiddenMerchants = {
      'search-trends': ['dvf', 'kosas', 'versedskin', 'yoox'],
      'cart-explorer': [],
    };

    /*
     * Names of params that are preserved in the query string.
     * merch_ids, search_term are handled by this component.
     * The rest are deferred to child components.
     * Ignore date params bc they're always last 30 days.
     */
    this.preservedKeys = [
      'merch_ids',
      'order_by',
      'direction',
      'page',
      'per_page',
      'search_term',
      'tab',
    ];
    this.gaPrefix = null;
    this.container = null;
    this.breakpointDown = 284; // Y position where we make search-bar fixed
    this.breakpointUp = 232; // Y position where we make search-bar static

    /*
     * One second debounce on query params update to prevent multiple
     * API calls when a user selects merchants on the carousel very quickly.
     * Wrap function around debounce for easier unit testing.
     */
    this.debounceSetQueryString = debounce(options => {
      this.setQueryString(options);
      this.AnalyticService.trackEvent(
        this.gaPrefix,
        'Click',
        `Logo Merchant Picker ${options.merch_ids}`,
      );
    }, 1000);
  }

  componentDidMount() {
    this.pubId = this.props.match.params.publisherId;
    this.AnalyticService.trackEvent(this.gaPrefix, 'Load', null);

    this.addScrollListener();

    // Don't query merchants if they already exist
    const merchantPromise = this.service.merchants.length
      ? Promise.resolve()
      : this.service.getMerchants();
    merchantPromise.then(() => this.getStateFromQueryString());
  }

  componentDidUpdate(prevProps) {
    const prevInsights = prevProps.location.pathname.split('/').pop();
    const currentInsights = this.props.location.pathname.split('/').pop();
    if (prevInsights !== currentInsights) {
      this.insightsType = currentInsights;
      const updatedMerchants = cloneDeep(this.state.merchants).map(merch =>
        this.checkIfMerchantHidden(merch),
      );

      // If hiding merchants causes none to be selected, select all
      if (!updatedMerchants.find(merchant => merchant.selected)) {
        updatedMerchants.map(merchant => {
          if (!merchant.hidden) {
            merchant.selected = true;
          }
          return merchant;
        });
      }
      this.updateMerchants(updatedMerchants);
    }
  }

  addScrollListener() {
    // Move addEventListener into this method for easier unit testing
    this.container.addEventListener('scroll', event => {
      const { scrollTop } = event.target;
      if (scrollTop >= this.breakpointDown && !this.state.scrollable) {
        this.setState({ scrollable: true });
      } else if (scrollTop < this.breakpointUp && this.state.scrollable) {
        this.setState({ scrollable: false });
      }
    });
  }

  /* Actions */
  getStateFromQueryString() {
    const searchParams = new URLSearchParams(this.props.location.search);

    let sortAvailable = false;
    const sortOptions = this.sortOptions.slice();
    sortOptions.forEach(option => {
      if (
        option.name === searchParams.get('order_by') &&
        option.direction === searchParams.get('direction')
      ) {
        option.selected = true;
        sortAvailable = true;
      } else {
        option.selected = false;
      }
    });
    if (!sortAvailable) sortOptions[0].selected = true;

    const merchants = this.getMerchantsFromQuery();
    const resetCarousel = merchants.length === merchants.filter(merch => merch.selected).length;

    // Preserve table state by getting params from the query string
    this.setState({
      searchTerm: searchParams.get('search_term'),
      merchants,
      currentTab: searchParams.get('tab') || this.tabOptions[0].name,
      loading: false,
      sortOptions,
      resetCarousel,
    });
  }

  setQueryString(options) {
    // Update URL query string with new values
    const queryStr = this.props.location.search.replace(/^\?/, '');
    const urlParams = queryString.parse(queryStr);
    Object.assign(urlParams, options);

    const searchParams = {};
    this.preservedKeys.forEach(key => {
      // Don't append search_term to query str if it's undefined/null
      if (urlParams[key]) {
        searchParams[key] = urlParams[key];
      }
      // Convert comma separated merch_ids to 'all' for brevity
      if (key === 'merch_ids') {
        searchParams[key] = this.getMerchIdStr();
      }
    });

    this.props.history.push({
      path: this.props.location.pathname,
      search: buildQueryString(searchParams),
    });
  }

  updateMerchants(newMerchants, carouselUpdate = false, resetCarousel = false) {
    this.setState(
      {
        merchants: newMerchants,
        resetCarousel,
      },
      () => {
        const options = { merch_ids: this.getCommaSeparatedMerchIds() };
        if (carouselUpdate) {
          this.debounceSetQueryString(options);
        } else {
          this.setQueryString(options);
        }
      },
    );
  }

  updateSearchTerm(searchTerm) {
    this.setQueryString({ search_term: searchTerm });
    this.setState({ searchTerm });
  }

  /* Helper functions */
  getCommaSeparatedMerchIds(getAllMerchants = false) {
    // Return IDs of selected merchants
    let { merchants } = this.state;
    if (!getAllMerchants) {
      merchants = merchants
        .filter(merchant => merchant.selected)
        .filter(merchant => !merchant.hidden);
    }

    return merchants.map(merchant => merchant.merch_id).join(',');
  }

  getMerchantsFromQuery() {
    /*
     * Return an array of merchants with correct selected state
     * based on merch_ids in the query string.
     */
    const allMerchants = this.service.merchants;
    const urlParams = new URLSearchParams(this.props.location.search).get('merch_ids');
    const merchIds =
      urlParams === 'all' || !urlParams ? null : urlParams.split(',').map(item => parseInt(item));

    const updatedMerchants = allMerchants.map(merch => {
      if (merchIds) {
        merch.selected = merchIds.includes(merch.merch_id);
      } else {
        // TODO: Return this to `['violetgrey']` after holiday season
        const defaultSelected = searchService.merchSlugIds.map(slugId => slugId.slug);

        merch.selected = urlParams === 'all' ? true : defaultSelected.includes(merch.slug);
      }
      merch = this.checkIfMerchantHidden(merch);

      return merch;
    });

    return updatedMerchants;
  }

  checkIfMerchantHidden(merch) {
    if (this.hiddenMerchants[this.insightsType]) {
      merch.hidden = this.hiddenMerchants[this.insightsType].includes(merch.slug);
      if (merch.hidden) {
        merch.selected = false;
      }
    } else {
      merch.hidden = false;
    }
    return merch;
  }

  getMerchIdStr() {
    // If all merchants are selected, convert comma-separated IDs to 'all'
    let merchIdsStr = this.getCommaSeparatedMerchIds();
    const liveMerchants = this.state.merchants.filter(merch => !merch.hidden);
    if (merchIdsStr.split(',').length === liveMerchants.length) {
      merchIdsStr = 'all';
    }

    return merchIdsStr;
  }

  updateDateRange(dateRangeItems) {
    const selected = dateRangeItems.filter(range => range.selected)[0].name;
    this.setState({
      currentDateRange: selected === 'thirtyDay' ? '30d' : '24h',
      dateRangeItems,
    });
  }

  changeTabs({ label, name }) {
    this.AnalyticService.trackEvent('Product Research', 'Click', `${label} tab`);

    const newSortOptions = this.state.sortOptions.map(sortOption => {
      return { ...sortOption, selected: sortOption.name === this.defaultSort };
    });
    this.setState({
      currentTab: name,
      sortOptions: newSortOptions,
    });
  }

  resetMerchantsSelected() {
    const newMerchants = cloneDeep(this.state.merchants);
    newMerchants.map(merch => (merch.selected = true));
    this.updateMerchants(newMerchants, false, true);
  }

  render() {
    let trends;
    let urlClass;
    const isLoading = this.state.loading || !this.state.merchants.length;
    this.gaPrefix =
      this.insightsType === 'cart-explorer'
        ? 'Cart Explorer'
        : this.insightsType === 'product-research'
        ? 'Product Research'
        : 'Search Trends';

    const merchantsSelected = this.state.merchants.filter(merch => merch.selected).length;

    const merchantsSelectedTxt = `(${
      this.state.merchants.length === merchantsSelected && this.state.resetCarousel
        ? `Viewing All Merchants)`
        : `${merchantsSelected} Merchant${merchantsSelected === 1 ? '' : 's'} Selected)`
    }`;

    const insightsPanel = !isLoading ? (
      <InsightsPanel
        merchants={this.state.merchants}
        data={
          ['cart-explorer', 'product-research'].includes(this.insightsType)
            ? this.state.productData
            : this.state.searchData
        }
        updateDateRange={this.updateDateRange.bind(this)}
        updateMerchants={this.updateMerchants.bind(this)}
        insightsType={this.insightsType}
        eventPrefix={this.gaPrefix}
        resetCarousel={this.state.resetCarousel}
      />
    ) : null;

    const tabOptions = this.tabOptions.map(option => {
      return (
        <div
          key={option.name}
          className={getClass(`tab-option ${option.name}`, {
            selected: this.state.currentTab === option.name,
            active: this.state.currentTab === option.name,
          })}
          onClick={() => this.changeTabs(option)}
        >
          {option.label}
        </div>
      );
    });

    if (this.insightsType === 'cart-explorer') {
      urlClass = 'explorer';
      trends = !isLoading ? (
        <CartExplorer
          location={this.props.location}
          urlQuery={this.props.location.search.slice(1)}
          pubId={this.pubId}
          merchants={this.state.merchants}
          merchIds={this.getCommaSeparatedMerchIds()}
          params={this.state.params}
          searchTerm={this.state.searchTerm}
          scrollable={this.state.scrollable}
          updateProductData={data => this.setState({ productData: data })}
          setQueryString={options => this.setQueryString(options)}
          currentDateRange={this.state.currentDateRange}
        />
      ) : null;
    } else if (this.insightsType === 'product-research') {
      urlClass = 'product-research';
      trends = !isLoading ? (
        <ProductResearch
          location={this.props.location}
          urlQuery={this.props.location.search.slice(1)}
          pubId={this.pubId}
          merchants={this.state.merchants}
          merchIds={this.getCommaSeparatedMerchIds()}
          params={
            this.state.currentDateRange === '24h' ? this.state.oneDayParams : this.state.params
          }
          searchTerm={this.state.searchTerm}
          scrollable={this.state.scrollable}
          updateProductData={data => this.setState({ productData: data })}
          setQueryString={options => this.setQueryString(options)}
          currentDateRange={this.state.currentDateRange}
          currentTab={this.state.currentTab}
          sortOptions={this.state.sortOptions}
          noResult={this.tabOptions.find(tab => tab.name === this.state.currentTab).noResult}
        />
      ) : null;
    } else {
      urlClass = 'search';
      trends = !isLoading ? (
        <SearchTrends
          location={this.props.location}
          urlQuery={this.props.location.search.slice(1)}
          pubId={this.pubId}
          merchants={this.state.merchants.filter(merch => merch.selected)}
          merchIds={this.getCommaSeparatedMerchIds()}
          params={this.state.params}
          searchTerm={this.state.searchTerm}
          scrollable={this.state.scrollable}
          updateSearchData={data => this.setState({ searchData: data })}
          setQueryString={options => this.setQueryString(options)}
        />
      ) : null;
    }

    const resetMerchantsPicker =
      this.state.merchants.length !== merchantsSelected ||
      (this.state.merchants.length === merchantsSelected && !this.state.resetCarousel) ? (
        <button className="reset" onClick={this.resetMerchantsSelected.bind(this)}>
          Reset
        </button>
      ) : null;

    const multipleMechantsPicker = (
      <button
        className="select-multiple-merchants"
        onClick={() => this.setState({ showMerchantModal: true })}
      >
        Filter by Multiple Merchants
      </button>
    );

    const merchantPickers =
      this.insightsType === 'product-research' ? (
        <>
          <div className="selection-items">
            <div className="tabs">{tabOptions}</div>
            <div className="dropdown-container">
              <div>
                <span className="label">Sort By:</span>
                <SortDropdown
                  gaEvent={{
                    category: 'Product Research',
                    label: 'Sort By',
                  }}
                  items={this.state.sortOptions}
                  updateSortOptions={sortOptions =>
                    this.setState({
                      sortOptions,
                    })
                  }
                />
              </div>
              <DateToggle
                gaEvent={{
                  category: 'Product Research',
                  label: 'Date Picker Toggle',
                }}
                items={this.state.dateRangeItems}
                updateDateToggle={this.updateDateRange.bind(this)}
              />
            </div>
          </div>
          <div className="header">
            <div className="text">
              <span>Filter By Merchant</span>
              <span className="quantity-selected">{merchantsSelectedTxt}</span>
            </div>
            <div className="multi-merchant-pickers">
              {multipleMechantsPicker}
              {resetMerchantsPicker}
            </div>
          </div>
          <MerchantCarousel
            insightsType={this.insightsType}
            merchants={this.state.merchants}
            updateMerchants={this.updateMerchants.bind(this)}
            resetCarousel={this.state.resetCarousel}
          />
        </>
      ) : null;

    const merchantSelectModal = this.state.showMerchantModal ? (
      <MerchantSelectModal
        merchantItems={this.state.merchants}
        updateMerchants={this.updateMerchants.bind(this)}
        closeModal={() => this.setState({ showMerchantModal: false })}
        resetCarousel={this.state.resetCarousel}
      />
    ) : null;

    return (
      <div
        ref={node => (this.container = node)}
        className={`insights-container ${urlClass} fs-unmask`}
      >
        {insightsPanel}
        <div className="insights">
          <TrendFilter
            searchTerm={this.state.searchTerm}
            updateSearch={term => this.updateSearchTerm(term)}
            scrollable={this.state.scrollable}
            eventPrefix={this.gaPrefix}
            insightsType={this.insightsType}
          />
          <div className="merchant-pickers">{merchantPickers}</div>
          {trends}
        </div>
        {merchantSelectModal}
      </div>
    );
  }
}

export default Insights;
